import { useState, useCallback, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { PATH_URL } from 'src/helpers/path'
import { getQueryParameters } from 'src/helpers/tool'
import { preserveUrlParams } from 'src/helpers/preserveUrlParams'
import { TableCountFormState, UseTableCountHookResult } from './types'

const INITIAL_FORM_STATE: TableCountFormState = {
  isSubmitting: false,
  error: null,
}

export const useTableCount = (initialValue = ''): UseTableCountHookResult => {
  const [tableCount, setTableCount] = useState(initialValue)
  const [formState, setFormState] = useState<TableCountFormState>(INITIAL_FORM_STATE)
  const history = useHistory()

  // Check for collectionId parameter and save it to collectionsQuizEntry
  useEffect(() => {
    const params = getQueryParameters()
    const collectionId = params.get('collectionId')

    if (collectionId) {
      console.log('Collection ID detected in TableCountPage, saving to collectionsQuizEntry')
      const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
      collectionsQuizEntry.collectionId = collectionId
      localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))
    }
  }, [])

  const validateTableCount = (value: string): string | null => {
    if (!value.trim()) {
      return 'Please enter the number of tables you will need'
    }

    const numValue = Number(value)
    if (isNaN(numValue)) {
      return 'Please enter a valid number'
    }

    if (!Number.isInteger(numValue)) {
      return 'Please enter a whole number'
    }

    if (numValue <= 0) {
      return 'Table count must be greater than 0'
    }

    return null
  }

  const handleTableCountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      if (!/^\d*$/.test(value) && value !== '') {
        return
      }
      setTableCount(value)
      if (formState.error) {
        setFormState((prev) => ({ ...prev, error: null }))
      }
    },
    [formState.error]
  )

  const handleSubmit = useCallback(async (): Promise<void> => {
    try {
      setFormState((prev) => ({ ...prev, isSubmitting: true, error: null }))

      const validationError = validateTableCount(tableCount)
      if (validationError) {
        setFormState((prev) => ({ ...prev, error: validationError, isSubmitting: false }))
        return
      }

      // Save table count to localStorage
      const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')

      const quizResponses = collectionsQuizEntry.quizResponses || []
      const tableCountIndex = quizResponses.findIndex((item: any) => item.key === 'TABLE_COUNT')
      const updatedQuizResponses =
        tableCountIndex > -1
          ? quizResponses.map((item: any, index: number) =>
              index === tableCountIndex ? { ...item, values: tableCount } : item
            )
          : [...quizResponses, { key: 'TABLE_COUNT', values: tableCount }]

      collectionsQuizEntry.quizResponses = updatedQuizResponses

      // Ensure collectionId is saved if present
      const params = getQueryParameters()
      const collectionId = params.get('collectionId') || collectionsQuizEntry.collectionId
      if (collectionId) {
        collectionsQuizEntry.collectionId = collectionId
      }

      localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))

      // Use preserveUrlParams to maintain query parameters
      const nextUrl = preserveUrlParams(PATH_URL.COLLECTIONS_QUIZ_FLORAL_PLANNING)
      history.push(nextUrl)
    } catch (error) {
      setFormState((prev) => ({
        ...prev,
        error: 'An error occurred while saving. Please try again.',
        isSubmitting: false,
      }))
    }
  }, [tableCount, history])

  return {
    formState,
    tableCount,
    handleTableCountChange,
    handleSubmit,
  }
}
